import React, { useState, useEffect, useCallback, useRef, createContext, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

// --- TYPES ---
interface TourStep {
  path: string;
  title: string;
  description: string;
  delay: number;
}

interface TourContextType {
  isTourActive: boolean;
  startTour: () => void;
  exitTour: () => void;
  nextStep: () => void;
  prevStep: () => void;
  currentStep: number;
  totalSteps: number;
  currentStepDelay: number;
}

// Calcula delay: ~300ms/palavra + 3s base de navegação/leitura do título
const readingDelay = (text: string, extraMs = 0) => {
  const words = text.trim().split(/\s+/).length;
  return Math.max(6000, words * 300 + 3000) + extraMs;
};

// --- CONSTANTS ---
const tourSteps: TourStep[] = [
  {
    path: '/',
    title: 'A Conexão',
    description: 'E se a tecnologia não fosse uma barreira, mas a ponte para o seu próximo nível? Meu trabalho é construir essa ponte.',
    delay: readingDelay('E se a tecnologia não fosse uma barreira, mas a ponte para o seu próximo nível? Meu trabalho é construir essa ponte.'),
  },
  {
    path: '/about',
    title: 'A Jornada',
    description: "Minha jornada na tecnologia me ensinou uma lição fundamental: o mais importante é 'aprender a aprender'.",
    delay: readingDelay("Minha jornada na tecnologia me ensinou uma lição fundamental: o mais importante é 'aprender a aprender'."),
  },
  {
    path: '/video1',
    title: 'IA em Ação',
    description: 'Veja como aplicar Inteligência Artificial no dia a dia dos negócios de forma prática e eficiente. Assista aos exemplos reais!',
    delay: readingDelay('Veja como aplicar Inteligência Artificial no dia a dia dos negócios de forma prática e eficiente. Assista aos exemplos reais!', 28000),
  },
  {
    path: '/video2',
    title: 'Resultados Comprovados',
    description: 'Cases de sucesso e transformações reais que a IA proporcionou. Números que falam por si só!',
    delay: readingDelay('Cases de sucesso e transformações reais que a IA proporcionou. Números que falam por si só!', 28000),
  },
  {
    path: '/projects',
    title: 'O Portfólio em Detalhes',
    description: 'Mentoria em IA, plataforma de cursos, sistemas cognitivos, GS1 Digital Link... Cada card conta uma solução real. Role e explore cada projeto.',
    delay: readingDelay('Mentoria em IA, plataforma de cursos, sistemas cognitivos, GS1 Digital Link... Cada card conta uma solução real. Role e explore cada projeto.', 22000),
  },
  {
    path: '/contact',
    title: 'O Próximo Passo',
    description: 'Sua jornada para o próximo nível começa com uma conversa. Vamos explorar como a tecnologia pode impulsionar seus resultados.',
    delay: readingDelay('Sua jornada para o próximo nível começa com uma conversa. Vamos explorar como a tecnologia pode impulsionar seus resultados.'),
  },
];

const TOUR_TOAST_ID = 'tour-toast'; // ID Fixo para o toast do tour

import { useScroll } from './useSmoothScroll';

// --- CONTEXT & PROVIDER ---
const TourContext = createContext<TourContextType | undefined>(undefined);

export const TourProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isTourActive, setIsTourActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const navigate = useNavigate();
  const tourTimerRef = useRef<NodeJS.Timeout | null>(null);
  const scrollTimerRef = useRef<NodeJS.Timeout | null>(null);
  const { lenis } = useScroll(); // Usar o hook do scroll

  const showToastForStep = useCallback((stepIndex: number) => {
    const step = tourSteps[stepIndex];
    if (step) {
      toast(step.title, {
        id: TOUR_TOAST_ID,
        description: step.description,
        duration: step.delay - 500,
        style: {
          background: 'rgba(11, 11, 11, 0.96)',
          color: '#F2EDE3',
          border: '1px solid #FF4500',
          fontFamily: "'JetBrains Mono', ui-monospace, monospace",
          fontSize: '0.95rem',
          padding: '1rem',
        },
      });
    }
  }, []);

  const exitTour = useCallback(() => {
    setIsTourActive(false);
    setCurrentStep(0);
    if (tourTimerRef.current) {
      clearTimeout(tourTimerRef.current);
      tourTimerRef.current = null;
    }
    if (scrollTimerRef.current) {
      clearTimeout(scrollTimerRef.current);
      scrollTimerRef.current = null;
    }
    // lenis?.stop(); // Removido - esta linha estava causando o bloqueio do scroll
    toast.dismiss(TOUR_TOAST_ID); // Dispensar o toast ao sair
    toast.info('Tour finalizado.');
  }, []);

  const nextStep = useCallback(() => {
    setCurrentStep(prev => {
      if (prev + 1 < tourSteps.length) {
        return prev + 1;
      }
      exitTour();
      return prev;
    });
  }, [exitTour]);

  const startTour = useCallback(() => {
    setIsTourActive(true);
    setCurrentStep(0);
    lenis?.start(); // Garantir que o scroll está ativo
  }, [lenis]);

  const prevStep = useCallback(() => {
    setCurrentStep(prev => (prev > 0 ? prev - 1 : prev));
  }, []);

  const savedCallback = useRef(nextStep);
  useEffect(() => {
    savedCallback.current = nextStep;
  }, [nextStep]);

  useEffect(() => {
    if (!isTourActive) return;

    const step = tourSteps[currentStep];
    if (!step) return;

    navigate(step.path);
    showToastForStep(currentStep);

    if (lenis) {
      lenis.scrollTo(0, { immediate: true, force: true });

      if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
      scrollTimerRef.current = setTimeout(() => {
        lenis.scrollTo('bottom', {
          duration: (step.delay / 1000) * 0.8,
          easing: (t) => t,
        });
      }, 700);
    }

    const tick = () => savedCallback.current();
    if (tourTimerRef.current) clearTimeout(tourTimerRef.current);
    tourTimerRef.current = setTimeout(tick, step.delay);

    return () => {
      if (tourTimerRef.current) clearTimeout(tourTimerRef.current);
      if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
    };
  }, [isTourActive, currentStep, navigate, showToastForStep, lenis]);

  const value = {
    isTourActive,
    startTour,
    exitTour,
    nextStep,
    prevStep,
    currentStep,
    totalSteps: tourSteps.length,
    currentStepDelay: tourSteps[currentStep]?.delay ?? 0,
  };

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
};

// --- HOOK ---
export const useTour = () => {
  const context = useContext(TourContext);
  if (context === undefined) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
};